/**
 * RegistrationModal Component
 * Allows users to request an account
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RegistrationModal.css';

/**
 * Human-readable student account length, e.g. 180 -> "about 6 months (180 days)"
 * @param {number|null} days
 * @returns {string}
 */
function describeStudentAccess(days) {
  if (!days) return 'a set period';
  if (days < 60) return `${days} days`;
  return `about ${Math.round(days / 30.4)} months (${days} days)`;
}

function RegistrationModal({ onClose }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    jobTitle: '',
    professionRole: 'sonographer',
    message: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  // Admin-configurable (Settings → Student Accounts); display only — the
  // server applies its own current value at signup
  const [studentAccessDays, setStudentAccessDays] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/register/student-access`)
      .then(res => setStudentAccessDays(res.data.studentAccessDays || null))
      .catch(() => setStudentAccessDays(null));
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user types
    if (error) setError('');
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // "Student Sonographer" is a UI-only pseudo-value of the same dropdown —
      // the server sees a normal sonographer plus a separate isStudent flag.
      const isStudent = formData.professionRole === 'student';
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/register`,
        { ...formData, professionRole: isStudent ? 'sonographer' : formData.professionRole, isStudent }
      );

      if (response.data.success) {
        setSuccess(response.data);
        // Auto-close after 5 seconds — unless the credentials email failed,
        // in which case the user needs time to read what to do next
        if (response.data.emailSent !== false) {
          setTimeout(() => {
            onClose();
          }, 5000);
        }
      } else {
        setError(response.data.message || 'Registration failed');
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Failed to submit registration request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content registration-modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-container">
            {success.autoApproved && success.emailSent === false ? (
              <>
                <div className="success-icon">⚠</div>
                <h2>Account Created — Email Not Sent</h2>
                <p>Your account was created, but we could not send the email with your login details to <strong>{formData.email}</strong>.</p>
                <p>Please contact your administrator — they can reset your password and give you your login details directly.</p>
              </>
            ) : success.autoApproved ? (
              <>
                <div className="success-icon">✓</div>
                <h2>Account Approved!</h2>
                <p>Your login details have been emailed to <strong>{formData.email}</strong>.</p>
                <p className="email-note">If you don't see it within a few minutes, check your junk/spam folder.</p>
              </>
            ) : (
              <>
                <div className="success-icon">✓</div>
                <h2>Registration Submitted!</h2>
                <p>Thank you for your registration request.</p>
                <p>An administrator will review your request and contact you via email.</p>
                <p className="email-note">Please check <strong>{formData.email}</strong> for updates.</p>
              </>
            )}
            <button onClick={onClose} className="btn-primary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content registration-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Account</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <p className="modal-description">
          Request access to the Ultrasound Worksheet System. An administrator will review 
          your request and send you login credentials via email.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fullName">
              Full Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="John Smith"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john.smith@example.com"
              required
            />
            <small>You'll receive your login credentials at this email</small>
          </div>

          <div className="form-group">
            <label htmlFor="jobTitle">Job Title</label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder="Sonographer"
            />
          </div>

          <div className="form-group">
            <label htmlFor="professionRole">
              I am a... <span className="required">*</span>
            </label>
            <select
              id="professionRole"
              name="professionRole"
              value={formData.professionRole}
              onChange={handleChange}
            >
              <option value="sonographer">Sonographer</option>
              <option value="nurse">Nurse (Lung Cancer Screening)</option>
              <option value="student">Student Sonographer (temporary account)</option>
            </select>
            {formData.professionRole === 'student' ? (
              <small>
                Creates a temporary account that's approved instantly and automatically
                deactivates after {describeStudentAccess(studentAccessDays)} — no need to contact an admin
                for access or removal. Your name will appear in the sonographer technologist list.
              </small>
            ) : (
              <small>Determines which worksheets you'll see and your default technologist list</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message">Message (Optional)</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us why you need access (optional)"
              rows="3"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="button-group">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
              Cancel
            </button>
          </div>
        </form>

        <div className="modal-footer">
          <p className="footer-note">
            Already have an account? <button className="link-btn" onClick={onClose}>Login here</button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegistrationModal;
